import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { TabPosition } from '@progress/kendo-angular-layout';
import { DataWithPagingMetadata } from '~/infrastructure/paging';
import { PageNames } from '~/models/pageNames';
import { PageState } from '~/shared/enums/stock-files.enum';
import {
  MaintenanceAssetScheduleDto,
  MaintenanceBlacklistCheckResultDto,
  MaintenanceTypeDto,
  MeasureWhenPerformedDto,
  PerformMaintenanceRequestModel,
  PerformMaintenanceActionModel
} from '~/models/modules/Asset/Transactions/Maintenance/maintenance.model';
import { FormatService } from '~/services/administration.ts/format';
import { UsersService } from '~/services/administration.ts/users';
import { AssetService } from '~/services/modules/assets/asset.service';
import { PerformMaintenanceService } from '~/services/modules/assets/transactions/perform-maintenance.service';
import { ScheduleMaintenanceService } from '~/services/modules/assets/transactions/schedule-maintenance.service';
import { BarcodeGeneratorsService } from '~/services/modules/stocks/barcode-generators';
import { SharedService } from '~/services/sharedService';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { NoteComponent } from '~/shared/components/note/note.component';
import { GDataService } from '~/services/GDataService';
import { SecurityService } from '~/services/security.service';


/**
 * @component PerformMaintenanceFormComponent
 * @description
 * Execution modal form for recording completed or in-progress maintenance on an asset.
 *
 * Key Capabilities:
 * - Maintenance Execution Logging:
 *   - Records maintenance date-time, labor costs, parts costs, total cost calculation, and notes.
 *   - Associates the execution with an asset and optionally links to an existing open maintenance schedule (`MaintenanceAssetScheduleDto`).
 * - Overdue / Blacklist Warning Detection:
 *   - Automatically calls `performService.checkCheckoutBlacklist(assetId)` on asset selection.
 *   - Displays an alert banner if the asset is overdue for scheduled maintenance.
 * - Recurrence Advancement:
 *   - When completing maintenance for a recurring schedule, enabling `advanceRecurrence` instructs the backend
 *     to calculate and schedule the next maintenance due date automatically.
 * - ComboBox Infinite-Scroll Lazy Loading:
 *   - Asset selector uses dynamic DOM scroll listeners on `.k-popup .k-list-content` via Angular's `Renderer2`.
 *   - Automatically requests subsequent 50-item pages as the user scrolls to the bottom of the list.
 * - Asset & Person Modal Selectors:
 *   - Supports full grid selection popups for searching and picking assets and technicians.
 */
@Component({
    selector: 'app-perform-maintenance-form',
    templateUrl: './perform-maintenance-form.component.html',
    styleUrls: ['./perform-maintenance-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PerformMaintenanceFormComponent implements OnInit, OnDestroy {
  /** Reference to the host Kendo UI window instance */
  @ViewChild('mainWindow') kendoMainWindow: any;
  @ViewChild('note') note: NoteComponent;


  /** Emitted when a maintenance execution is successfully saved */
  @Output() saved = new EventEmitter<any>();

  /** Emitted when the user cancels or closes the dialog */
  @Output() cancelled = new EventEmitter<void>();

  _active: boolean = true;
  _theme: string = '';
  _page: string = PageNames.PerformMaintenance;
  _pageState: PageState = PageState.ADD;
  PageState = PageState;
  isPerformAction: boolean = false;
  actionType: 'ADD' | 'PERFORM' | 'EDIT' | 'VIEW' = 'ADD';
  _form: FormGroup;
  _datePickerFormat: string = 'MM/dd/yyyy hh:mm a';
  _costFormat: string = 'c2';
  _invalidForm: boolean = false;
  isSubmitting: boolean = false;

  isDetailsExpanded: boolean = true;
  isCostExpanded: boolean = true;
  isDocumentsExpanded: boolean = true;
  isNotesExpanded: boolean = true;

  // TabStrip & Form State
  _kendoTabStripPosition: TabPosition = 'left';
  _selectedTab: string = 'Documents';
  _performedFilesDoc: any[] = [];
  _model: any = { id: 0 };
  _selectedAssetList: any[] = [];

  assetsData: any[] = [];
  assetsDataFiltered: any[] = [];
  maintenanceTypes: MaintenanceTypeDto[] = [];
  maintenanceTypesFiltered: MaintenanceTypeDto[] = [];
  measuresWhenPerformed: MeasureWhenPerformedDto[] = [];
  measuresWhenPerformedFiltered: MeasureWhenPerformedDto[] = [];
  personsData: any[] = [];
  personsDataFiltered: any[] = [];
  allOpenSchedules: MaintenanceAssetScheduleDto[] = [];
  openSchedules: MaintenanceAssetScheduleDto[] = [];
  openSchedulesFiltered: MaintenanceAssetScheduleDto[] = [];
  selectedSchedule: MaintenanceAssetScheduleDto | null = null;
  blacklistInfo: MaintenanceBlacklistCheckResultDto | null = null;

  // Dialog handling (Maintenance Type creation/edit)
  _isDialiog: boolean = false;
  _dialogComponentName: string = '';
  _dialogModel: any = {};

  /** Paging parameters for the lazy-loaded asset combobox */
  _assetComboBoxPagingParams = {
    currentItem: '0',
    totalbyPage: 50,
    search: '',
    page: 1
  };
  _allAssetCount: number = 0;
  _assetComboScroll: boolean = false;
  isOpened: boolean = false;
  clearTimeout: any;
  private _isLoadingAssets: boolean = false;
  private _assetFilterTimeout: any;
  private _scrollUnlisten?: () => void;

  // #region Asset Select Popup
  _assetsListEnabled: boolean = false;
  _selectedAssets: any[] = [];
  // #endregion

  // #region Person Select Popup
  _personsListEnabled: boolean = false;
  _selectedPerson: any = null;
  // #endregion

  constructor(
    private _fb: FormBuilder,
    private performService: PerformMaintenanceService,
    private scheduleService: ScheduleMaintenanceService,
    private assetService: AssetService,
    private usersService: UsersService,
    private _barcodeGeneratorsService: BarcodeGeneratorsService,
    private _formatService: FormatService,
    private _themeManager: ThemeManagerService,
    private _sharedService: SharedService,
    private _layoutComponent: LayoutComponent,
    private _renderer2: Renderer2,
    private _dataService: GDataService,
    private _securityService: SecurityService
  ) {
    this._theme = this._themeManager?.themeChange?.value || 'default';
    this._themeManager.themeChange.subscribe((data) => {
      if (data) this._theme = data;
    });
  }

  /**
   * Component initialization.
   * Builds the reactive form, loads user date/cost formats, populates dropdowns,
   * and hydrates initial model data if supplied.
   */
  ngOnInit(): void {
    if (window.innerWidth <= 991) {
      this._kendoTabStripPosition = 'top';
    } else {
      this._kendoTabStripPosition = 'left';
    }
    this.initForm();
    this.loadFormats();
    this.loadDropdowns();
    this.checkSharedModel();
  }

  /**
   * Cleans up pending debounce timers and unlistens from active DOM scroll listeners.
   */
  ngOnDestroy(): void {
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    if (this._assetFilterTimeout) clearTimeout(this._assetFilterTimeout);
    if (this._scrollUnlisten) {
      this._scrollUnlisten();
      this._scrollUnlisten = undefined;
    }
  }

  /**
   * Handles Kendo window state changes (minimize, maximize, restore)
   * to toggle responsive CSS classes.
   * @param e Window state change event
   */
  _onStateChanges(e: any): void {
    const element: any = this.kendoMainWindow;
    if (!element?.el?.nativeElement) return;
    if (e === 'minimized' || e?.minimized) {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else if (e === 'maximized' || e?.maximized) {
      element.el.nativeElement.classList.remove('fullWidthFormForm');
    } else {
      element.el.nativeElement.classList.add('fullWidthFormForm');
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth <= 991) {
      this._kendoTabStripPosition = 'top';
    } else {
      this._kendoTabStripPosition = 'left';
    }
  }

  _onTabSelect(e: any): void {
    this._selectedTab = e?.title || 'Documents';
  }


  /**
   * Initializes the reactive form controls with validators and initial values.
   * Sets default perform date to today in the user's configured timezone.
   * Listens to assetId changes to load open schedules and check blacklist status.
   */
  private initForm(): void {
    const nowInUserTz = this._sharedService?.getTodayInUserTimezone
      ? this._sharedService.getTodayInUserTimezone()
      : new Date();

    const currentPerson = this._securityService?.currentUser?.person;
    const defaultPersonId = currentPerson?.id || null;
    if (currentPerson && defaultPersonId) {
      const code = currentPerson.code || '';
      const first = currentPerson.firstName || '';
      const last = currentPerson.lastName || '';
      const mapped = {
        ...currentPerson,
        id: defaultPersonId,
        displayName: `${code ? code + ' - ' : ''}${first} ${last}`.trim() || code || `Person #${defaultPersonId}`,
        fullName: `${first} ${last}`.trim() || code || `Person #${defaultPersonId}`
      };
      if (!this.personsData.some((p) => Number(p.id) === Number(defaultPersonId))) {
        this.personsData.unshift(mapped);
        this.personsDataFiltered = [...this.personsData];
      }
    }

    this._form = this._fb.group({
      maintenanceCode: [{ value: '', disabled: false }, Validators.required],
      assetId: [{ value: null, disabled: false }, Validators.required],
      maintenanceTypeId: [{ value: null, disabled: false }, Validators.required],
      timeInterval: [{ value: true, disabled: false }],
      performNonDate: [{ value: null, disabled: false }],
      measureWhenPerformedId: [{ value: null, disabled: false }],
      maintenanceAssetScheduleId: [{ value: null, disabled: false }],
      personId: [{ value: defaultPersonId, disabled: false }, Validators.required],
      performDate: [{ value: nowInUserTz, disabled: false }, Validators.required],
      laborCost: [{ value: 0, disabled: false }, [Validators.min(0)]],
      partsCost: [{ value: 0, disabled: false }, [Validators.min(0)]],
      advanceRecurrence: [{ value: true, disabled: false }],
      notes: [{ value: '', disabled: false }],
      maintenanceNotes: [{ value: '', disabled: false }]
    });

    this.applyControlStates();

    if (this.actionType !== 'VIEW' && !this._form.get('maintenanceCode')?.value) {
      this._generateBarcodeClick();
    }

    this._form.get('timeInterval')?.valueChanges.subscribe((isDate) => {
      const nonDateControl = this._form.get('performNonDate');
      const measureControl = this._form.get('measureWhenPerformedId');
      const performDateControl = this._form.get('performDate');
      if (isDate === false) {
        measureControl?.setValidators([Validators.required]);
        performDateControl?.clearValidators();
      } else {
        measureControl?.clearValidators();
        measureControl?.setValue(null);
        nonDateControl?.setValue(null);
        performDateControl?.setValidators([Validators.required]);
        if (!performDateControl?.value) {
          const now = this._sharedService?.getTodayInUserTimezone
            ? this._sharedService.getTodayInUserTimezone()
            : new Date();
          performDateControl?.setValue(now);
        }
      }
      nonDateControl?.updateValueAndValidity();
      measureControl?.updateValueAndValidity();
      performDateControl?.updateValueAndValidity();
    });

    // Auto-populate maintenanceCode if currently empty
    if (!this._form.get('maintenanceCode')?.value) {
      this._generateBarcodeClick();
    }

    this._form.get('assetId')?.valueChanges.pipe(distinctUntilChanged()).subscribe((assetId) => {
      this.updateSelectedAssetList(assetId);
      this.updateAvailableSchedules();
      if (assetId) {
        this.onAssetSelected(assetId);
      } else {
        this.blacklistInfo = null;
      }
    });
    this._form.get('maintenanceAssetScheduleId')?.valueChanges.pipe(distinctUntilChanged()).subscribe((scheduleId) => {
      if (!scheduleId) {
        this.selectedSchedule = null;
        return;
      }
      this.selectedSchedule =
        this.allOpenSchedules.find((s) => s.id === scheduleId) ||
        this.openSchedules.find((s) => s.id === scheduleId) ||
        null;
      if (this.selectedSchedule) {
        this.applyScheduleToForm(this.selectedSchedule);
      } else {
        this.scheduleService.getScheduleById(scheduleId).subscribe({
          next: (res: any) => {
            const s = res?.data || res?.body || res;
            if (s) {
              this.applyScheduleToForm(s);
              if (!this.allOpenSchedules.some((item) => item.id === s.id)) {
                const mapped = {
                  ...s,
                  displayTitle: `${s.maintenanceCode ? s.maintenanceCode + ' - ' : ''}${s.maintenanceTypeName || ('Schedule #' + s.id)}${s.assetNumber ? ' [' + s.assetNumber + ']' : ''}`
                };
                this.allOpenSchedules.unshift(mapped);
                this.openSchedules.unshift(mapped);
                this.openSchedulesFiltered = [...this.openSchedules];
              }
            }
          }
        });
      }
    });
  }

  /**
   * Configures form control enable/disable states:
   * - VIEW mode (PageState.VIEW): All fields are disabled (read-only).
   * - ADD, PERFORM, or EDIT mode: Fields are enabled for user interaction.
   */
  applyControlStates(): void {
    if (!this._form) return;

    if (this._pageState === PageState.VIEW) {
      this._form.disable();
    } else {
      this._form.enable();
    }
  }

  /**
   * Pre-populates perform maintenance form with the selected schedule's data:
   * Maintenance Code, Performed By person, Performed date (scheduled date),
   * Asset, Maintenance Type, Recurrence, Parts, and Attachments.
   * @param schedule Schedule record DTO.
   */
  applyScheduleToForm(schedule: any): void {
    debugger
    if (!schedule) return;
    this.selectedSchedule = schedule;

    // 1. Maintenance Code
    if (schedule.maintenanceCode) {
      this._form.patchValue({ maintenanceCode: schedule.maintenanceCode });
    }

    // 2. Performed By Person (Schedule assigned person)
    if (schedule.personId) {
      this.ensurePersonLoaded(schedule.personId, schedule.person);
      this._form.patchValue({ personId: schedule.personId });
    }

    // 3. Performed Date (Pre-populate with scheduled date)
    const schedDate = schedule.scheduledFor || schedule.dueNext || schedule.scheduledOn;
    if (schedDate) {
      const parsedDate = new Date(schedDate);
      if (!isNaN(parsedDate.getTime())) {
        this._form.patchValue({ performDate: parsedDate });
      }
    }

    // 4. Asset
    if (schedule.assetId && this._form.get('assetId')?.value !== schedule.assetId) {
      this.ensureAssetLoaded(schedule.assetId);
      this._form.patchValue({ assetId: schedule.assetId }, { emitEvent: false });
      this.updateSelectedAssetList(schedule.assetId);
      this.performService.checkCheckoutBlacklist(schedule.assetId).subscribe({
        next: (blacklist) => {
          this.blacklistInfo = blacklist?.isBlocked ? blacklist : null;
        }
      });
    }

    // 5. Maintenance Type
    if (schedule.maintenanceTypeId) {
      this._form.patchValue({ maintenanceTypeId: schedule.maintenanceTypeId });
    }

    // 6. Time Interval
    if (schedule.timeInterval !== undefined && schedule.timeInterval !== null) {
      this._form.patchValue({ timeInterval: schedule.timeInterval });
    }

    // 7. Non-Date intervals
    if (schedule.scheduledForOtherInterval != null || schedule.performNonDate != null) {
      this._form.patchValue({ performNonDate: schedule.scheduledForOtherInterval ?? schedule.performNonDate });
    }
    if (schedule.measureWhenPerformedId != null || schedule.maintenanceIntervalId != null) {
      this._form.patchValue({ measureWhenPerformedId: schedule.measureWhenPerformedId ?? schedule.maintenanceIntervalId });
    }

    // 8. Recurrence
    this._form.patchValue({ advanceRecurrence: !!schedule.isRecurring });

    // 9. Files / Attachments
    if (schedule.files && schedule.files.length > 0 && (!this._performedFilesDoc || this._performedFilesDoc.length === 0)) {
      this._performedFilesDoc = schedule.files.map((f: any) => ({
        fileId: f.fileId || f.id || f.file?.id,
        file: f.file || f,
        name: f.file?.name || f.name,
        description: f.file?.description || f.description,
        type: f.file?.type || f.type,
        stream: f.file?.stream || f.stream
      }));
    }

    // 10. Estimated Parts Cost
    if (schedule.stockParts && schedule.stockParts.length > 0 && !this._form.get('partsCost')?.value) {
      const estimatedPartsCost = schedule.stockParts.reduce((sum: number, sp: any) => {
        const qty = Number(sp.requiredQuantity) || 0;
        const cost = Number(sp.cost) || 0;
        return sum + (sp.extendedCost != null ? Number(sp.extendedCost) : qty * cost);
      }, 0);
      if (estimatedPartsCost > 0) {
        this._form.patchValue({ partsCost: Math.round(estimatedPartsCost * 100) / 100 });
      }
    }

    this.applyControlStates();
  }

  /**
   * Updates the selected asset list displayed in the General tab's grid.
   * @param assetId Selected asset ID or null.
   */
  updateSelectedAssetList(assetId: number | null): void {
    if (!assetId) {
      this._selectedAssetList = [];
      return;
    }
    const found = this.assetsData?.find((a) => a.id === assetId);
    if (found) {
      this._selectedAssetList = [found];
    } else {
      this.assetService.getAssetById(assetId).subscribe({
        next: (res: any) => {
          const asset = res?.data || res?.body || res;
          if (asset && asset.id) {
            this._selectedAssetList = [asset];
          }
        }
      });
    }
  }

  /**
   * Clears the selected asset when removed from the Selected Asset grid.
   */
  _clearSelectedAsset(): void {
    this._form.patchValue({ assetId: null });
    this._selectedAssetList = [];
  }

  /**
   * Loads date and currency formatting tokens from FormatService.
   */
  private loadFormats(): void {
    this._datePickerFormat = this._formatService.dateFormat || 'MM/dd/yyyy hh:mm a';
    this._costFormat = this._formatService.costFormatValue || 'c2';
  }

  /**
   * Initiates dropdown data retrieval: triggers asset pagination query and loads active persons.
   */
  private loadDropdowns(): void {
    this.getAssetsDD();
    this.loadAllSchedules();
    this.loadMaintenanceTypes();
    this.loadMeasuresWhenPerformed();

    this.usersService.getActivePersons().subscribe({
      next: (res: any) => {
        const raw = res?.body || res?.data || res || [];
        const fetched = (Array.isArray(raw) ? raw : []).map((p: any) => ({
          ...p,
          displayName: `${p.code ? p.code + ' - ' : ''}${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || '',
          fullName: `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.code || ''
        }));
        const preserved = this.personsData.filter((p) => !fetched.some((f: any) => Number(f.id) === Number(p.id)));
        this.personsData = [...preserved, ...fetched];
        this.personsDataFiltered = [...this.personsData];
      }
    });
  }

  /**
   * Checks if an asset, schedule, or person was passed via SharedService.
   * Automatically pre-populates form controls and loads referenced entities.
   */
  private checkSharedModel(): void {
    const sharedModel = this._sharedService?.model;
    const raw = sharedModel?.model !== undefined ? sharedModel.model : sharedModel;
    const isNew = sharedModel?.isNew ?? (sharedModel?.pageState === PageState.ADD || !raw?.id);
    if (raw) {
      if (raw.id && !isNew && (sharedModel?.pageState === PageState.EDIT || sharedModel?.pageState === PageState.VIEW)) {
        this._model = { id: raw.id };
        this._pageState = sharedModel?.pageState || PageState.EDIT;
        this.actionType = this._pageState === PageState.VIEW ? 'VIEW' : 'EDIT';
        this.isPerformAction = false;
      } else {
        this._model = { id: (raw.id && !isNew) ? raw.id : 0 };
        this._pageState = sharedModel?.pageState || PageState.ADD;
        this.isPerformAction = raw.isPerformAction ?? (raw.actionType === 'PERFORM' || !!raw.maintenanceAssetScheduleId || !!raw.scheduleId || !!raw.schedule);
        this.actionType = this.isPerformAction ? 'PERFORM' : 'ADD';
      }

      this.applyControlStates();

      // 1. Maintenance Code
      if (raw.maintenanceCode) {
        this._form.patchValue({ maintenanceCode: raw.maintenanceCode });
      }

      // 2. Asset
      const assetId = raw.assetId || (raw.asset ? raw.asset.id : null);
      if (assetId) {
        this.ensureAssetLoaded(assetId, raw.asset || (raw.assetNumber ? { id: assetId, assetNumber: raw.assetNumber, name: raw.assetName } : undefined));
        this._form.patchValue({ assetId }, { emitEvent: false });
        this.updateSelectedAssetList(assetId);
      }

      // 3. Maintenance Type
      const typeId = raw.maintenanceTypeId || (raw.maintenanceType ? raw.maintenanceType.id : null);
      if (typeId) {
        this.ensureMaintenanceTypeLoaded(typeId, raw.maintenanceType || (raw.maintenanceTypeName ? { id: typeId, maintenanceTypeName: raw.maintenanceTypeName, maintenanceTypeCode: raw.maintenanceTypeCode } : undefined));
        this._form.patchValue({ maintenanceTypeId: typeId });
      }

      // 4. Time Interval
      if (raw.timeInterval !== undefined && raw.timeInterval !== null) {
        this._form.patchValue({ timeInterval: Boolean(raw.timeInterval) });
      }

      // 5. Non-Date intervals
      if (raw.performNonDate != null || raw.scheduledForOtherInterval != null) {
        this._form.patchValue({ performNonDate: raw.performNonDate ?? raw.scheduledForOtherInterval });
      }
      const measureId = raw.measureWhenPerformedId || raw.maintenanceIntervalId || (raw.measureWhenPerformed ? raw.measureWhenPerformed.id : null);
      if (measureId) {
        this.ensureMeasureLoaded(measureId, raw.measureWhenPerformed || raw.maintenanceInterval);
        this._form.patchValue({ measureWhenPerformedId: measureId });
      }

      // 6. Person
      const personId = raw.personId || (raw.person ? raw.person.id : null);
      if (personId) {
        this.ensurePersonLoaded(personId, raw.person || (raw.personName ? { id: personId, fullName: raw.personName, displayName: raw.personName } : undefined));
        this._form.patchValue({ personId });
      }

      // 7. Perform Date
      const dateVal = raw.performDate || raw.scheduledFor || raw.dueNext || raw.scheduledOn;
      if (dateVal) {
        const d = new Date(dateVal);
        if (!isNaN(d.getTime())) {
          this._form.patchValue({ performDate: d });
        }
      }

      // 8. Costs
      if (raw.laborCost != null && raw.laborCost !== '') {
        this._form.patchValue({ laborCost: Number(raw.laborCost) });
      }
      if (raw.partsCost != null && raw.partsCost !== '') {
        this._form.patchValue({ partsCost: Number(raw.partsCost) });
      }

      // 9. Notes & Maintenance Notes
      if (raw.notes != null) {
        this._form.patchValue({ notes: raw.notes });
      }
      if (raw.maintenanceNotes != null) {
        this._form.patchValue({ maintenanceNotes: raw.maintenanceNotes });
      }

      // 10. Advance Recurrence
      if (raw.advanceRecurrence !== undefined && raw.advanceRecurrence !== null) {
        this._form.patchValue({ advanceRecurrence: Boolean(raw.advanceRecurrence) });
      } else if (raw.isRecurring !== undefined && raw.isRecurring !== null) {
        this._form.patchValue({ advanceRecurrence: Boolean(raw.isRecurring) });
      }

      // 11. Files / Attachments
      if (raw.files && Array.isArray(raw.files)) {
        this._performedFilesDoc = raw.files.map((f: any) => ({
          fileId: f.fileId || f.id || f.file?.id,
          file: f.file || f,
          name: f.file?.name || f.name,
          description: f.file?.description || f.description,
          type: f.file?.type || f.type,
          stream: f.file?.stream || f.stream
        }));
      }

      // 12. Schedule Details
      if (raw.schedule) {
        this.applyScheduleToForm(raw.schedule);
      } else if (raw.maintenanceAssetSchedule) {
        this.applyScheduleToForm(raw.maintenanceAssetSchedule);
      } else if (this.isPerformAction) {
        this.applyScheduleToForm(raw);
      }
      const scheduleId = raw.maintenanceAssetSchedule?.id;

      if (scheduleId) {
        this.ensureScheduleLoaded(scheduleId, raw.schedule || raw.maintenanceAssetSchedule || (this.isPerformAction ? raw : undefined));
        this._form.patchValue({ maintenanceAssetScheduleId: scheduleId });
      }
    }
  }

  /**
   * Ensures the given asset is present in `assetsData` for combobox selection display.
   * If not already cached in memory, fetches it directly by ID.
   * @param assetId Asset ID
   * @param assetObj Optional asset entity object for immediate display
   */
  ensureAssetLoaded(assetId: number, assetObj?: any): void {
    if (!assetId) return;
    if (assetObj) {
      const asset = {
        ...assetObj,
        id: assetId,
        assetNumber: assetObj.assetNumber || `Asset #${assetId}`,
        name: assetObj.name || assetObj.assetName || ''
      };
      if (!this.assetsData.some((a) => a.id === assetId)) {
        this.assetsData.unshift(asset);
        this.assetsDataFiltered = [...this.assetsData];
      }
      this._selectedAssetList = [asset];
      return;
    }

    if (!this.assetsData.some((a) => a.id === assetId)) {
      this.assetService.getAssetById(assetId).subscribe({
        next: (res: any) => {
          const asset = res?.data || res?.body || res;
          if (asset && asset.id) {
            this.assetsData.unshift(asset);
            this.assetsDataFiltered = [...this.assetsData];
            this._selectedAssetList = [asset];
          }
        }
      });
    } else {
      const found = this.assetsData.find((a) => a.id === assetId);
      if (found) {
        this._selectedAssetList = [found];
      }
    }
  }

  /**
   * Ensures the given maintenance type is present in `maintenanceTypes` for combobox selection display.
   * @param typeId Maintenance Type ID
   * @param typeObj Optional maintenance type entity object
   */
  ensureMaintenanceTypeLoaded(typeId: number, typeObj?: any): void {
    if (!typeId) return;
    if (typeObj) {
      const mapped: MaintenanceTypeDto = {
        ...typeObj,
        id: typeId,
        maintenanceTypeName: typeObj.maintenanceTypeName || typeObj.name || 'Maintenance Type',
        maintenanceTypeCode: typeObj.maintenanceTypeCode || typeObj.code || ''
      };
      if (!this.maintenanceTypes.some((t) => t.id === typeId)) {
        this.maintenanceTypes.unshift(mapped);
        this.maintenanceTypesFiltered = [...this.maintenanceTypes];
      }
      return;
    }

    if (!this.maintenanceTypes.some((t) => t.id === typeId)) {
      this.scheduleService.getMaintenanceTypes().subscribe({
        next: (res: any) => {
          const types = Array.isArray(res) ? res : (res?.data || []);
          types.forEach((t: any) => {
            if (!this.maintenanceTypes.some((existing) => existing.id === t.id)) {
              this.maintenanceTypes.push(t);
            }
          });
          this.maintenanceTypesFiltered = [...this.maintenanceTypes];
        }
      });
    }
  }

  /**
   * Ensures the given measure unit is present in `measuresWhenPerformed` for combobox selection display.
   * @param measureId Measure ID
   * @param measureObj Optional measure entity object
   */
  ensureMeasureLoaded(measureId: number, measureObj?: any): void {
    if (!measureId) return;
    if (measureObj) {
      const mapped = {
        ...measureObj,
        id: measureId,
        name: measureObj.name || measureObj.code || `Measure #${measureId}`,
        code: measureObj.code || ''
      };
      if (!this.measuresWhenPerformed.some((m) => m.id === measureId)) {
        this.measuresWhenPerformed.unshift(mapped);
        this.measuresWhenPerformedFiltered = [...this.measuresWhenPerformed];
      }
    }
  }

  /**
   * Ensures the given person is present in `personsData` for combobox selection display.
   * If not already cached in memory, fetches it directly by ID.
   * @param personId Person ID
   */
  ensurePersonLoaded(personId: number | string, personObj?: any): void {
    const numId = Number(personId);
    if (!numId) return;

    if (personObj) {
      const code = personObj.code || personObj.personCode || '';
      const first = personObj.firstName || '';
      const last = personObj.lastName || '';
      const mapped = {
        ...personObj,
        id: numId,
        displayName: `${code ? code + ' - ' : ''}${first} ${last}`.trim() || code || `Person #${numId}`,
        fullName: `${first} ${last}`.trim() || code || `Person #${numId}`
      };
      if (!this.personsData.some((p) => Number(p.id) === numId)) {
        this.personsData.unshift(mapped);
        this.personsDataFiltered = [...this.personsData];
      }
      return;
    }

    if (!this.personsData.some((p) => Number(p.id) === numId)) {
      this.usersService.getPersonById(numId).subscribe({
        next: (res: any) => {
          const p = res?.data || res?.body || res;
          if (p && (p.id || p.code)) {
            const code = p.code || p.personCode || '';
            const first = p.firstName || '';
            const last = p.lastName || '';
            const mapped = {
              ...p,
              id: Number(p.id) || numId,
              displayName: `${code ? code + ' - ' : ''}${first} ${last}`.trim() || code || `Person #${numId}`,
              fullName: `${first} ${last}`.trim() || code || `Person #${numId}`
            };
            if (!this.personsData.some((existing) => Number(existing.id) === numId)) {
              this.personsData.unshift(mapped);
              this.personsDataFiltered = [...this.personsData];
            }
          }
        }
      });
    }
  }

  /**
   * Ensures the given schedule is present in `allOpenSchedules` and `openSchedulesFiltered` for combobox selection display.
   * If not already loaded, fetches it directly by ID from `scheduleService`.
   * @param scheduleId Schedule ID
   * @param scheduleObj Optional schedule entity object
   */
  ensureScheduleLoaded(scheduleId: number | string, scheduleObj?: any): void {
    const numId = Number(scheduleId);
    if (!numId) return;

    if (scheduleObj) {
      const code = scheduleObj.maintenanceCode || scheduleObj.code || '';
      const typeName = scheduleObj.maintenanceTypeName || scheduleObj.maintenanceType?.name || scheduleObj.maintenanceType?.maintenanceTypeName || '';
      const assetNum = scheduleObj.assetNumber || scheduleObj.asset?.assetNumber || scheduleObj.asset?.assetCode || '';
      const mapped = {
        ...scheduleObj,
        id: numId,
        displayTitle: `${code ? code + ' - ' : ''}${typeName || ('Schedule #' + numId)}${assetNum ? ' [' + assetNum + ']' : ''}`
      };
      if (!this.allOpenSchedules.some((s) => Number(s.id) === numId)) {
        this.allOpenSchedules.unshift(mapped);
      }
      if (!this.openSchedules.some((s) => Number(s.id) === numId)) {
        this.openSchedules.unshift(mapped);
      }
      this.openSchedulesFiltered = [...this.openSchedules];
      this.selectedSchedule = mapped;
      return;
    }

    if (!this.allOpenSchedules.some((s) => Number(s.id) === numId)) {
      this.scheduleService.getScheduleById(numId).subscribe({
        next: (res: any) => {
          const s = res?.data || res?.body || res;
          if (s) {
            const code = s.maintenanceCode || s.code || '';
            const typeName = s.maintenanceTypeName || s.maintenanceType?.name || s.maintenanceType?.maintenanceTypeName || '';
            const assetNum = s.assetNumber || s.asset?.assetNumber || s.asset?.assetCode || '';
            const mapped = {
              ...s,
              id: numId,
              displayTitle: `${code ? code + ' - ' : ''}${typeName || ('Schedule #' + numId)}${assetNum ? ' [' + assetNum + ']' : ''}`
            };
            if (!this.allOpenSchedules.some((existing) => Number(existing.id) === numId)) {
              this.allOpenSchedules.unshift(mapped);
            }
            if (!this.openSchedules.some((existing) => Number(existing.id) === numId)) {
              this.openSchedules.unshift(mapped);
            }
            this.openSchedulesFiltered = [...this.openSchedules];
            this.selectedSchedule = mapped;
          }
        }
      });
    }
  }

  /**
   * Queries the backend for paged asset data with search filtering.
   * When `_assetComboScroll` is true, appends unique results to `assetsData` (infinite scrolling).
   * Otherwise, replaces the list (initial load or new search query).
   */
  getAssetsDD(): void {
    if (this._isLoadingAssets) return;
    this._isLoadingAssets = true;
    this.assetService.getAssetList(this._assetComboBoxPagingParams).subscribe({
      next: (res: any) => {
        this._isLoadingAssets = false;
        this._allAssetCount = res?.body?.returnCount ?? 0;
        const incoming = res?.body?.data || res?.body || [];
        if (!this._assetComboScroll) {
          const currentId = this._form?.get('assetId')?.value;
          const currentAsset = currentId ? this.assetsData.find((a) => a.id === currentId) : null;
          if (currentAsset && !incoming.some((a: any) => a.id === currentId)) {
            this.assetsData = [currentAsset, ...incoming];
          } else {
            this.assetsData = [...incoming];
          }
          this.assetsDataFiltered = [...this.assetsData];
          if (currentId && this._selectedAssetList.length === 0) {
            this.updateSelectedAssetList(currentId);
          }
        } else {
          const existingIds = new Set(this.assetsData.map((a) => a.id));
          const newItems = incoming.filter((a: any) => !existingIds.has(a.id));
          this.assetsData = this.assetsData.concat(newItems);
          this.assetsDataFiltered = [...this.assetsData];
        }
      },
      error: () => {
        this._isLoadingAssets = false;
      }
    });
  }

  /**
   * Attaches a DOM scroll listener to the Kendo ComboBox popup container (`.k-list-content`).
   * When user scrolls within 15px of the bottom, increments page and fetches additional assets.
   * Uses Angular's Renderer2 for clean event listener registration.
   * @param event Kendo ComboBox open event
   */
  scrollCombobox(event: any): void {
    this.isOpened = true;
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    this.clearTimeout = setTimeout(() => {
      if (this.isOpened) {
        const domobject: any =
          document.querySelector('.k-popup .k-list-content') ||
          document.querySelector('.k-list-content') ||
          document.querySelector('.k-list-scroller');
        if (domobject) {
          if (this._scrollUnlisten) {
            this._scrollUnlisten();
            this._scrollUnlisten = undefined;
          }
          this._scrollUnlisten = this._renderer2.listen(domobject, 'scroll', (e: any) => {
            if (!this._isLoadingAssets && this.assetsData.length < this._allAssetCount) {
              if (
                e.target.offsetHeight + e.target.scrollTop >= e.target.scrollHeight - 15 ||
                e.target.clientHeight + e.target.scrollTop >= e.target.scrollHeight - 15
              ) {
                this._assetComboScroll = true;
                this._assetComboBoxPagingParams.currentItem = (
                  parseInt(this._assetComboBoxPagingParams.currentItem) +
                  this._assetComboBoxPagingParams.totalbyPage
                ).toString();
                this._assetComboBoxPagingParams.page++;
                this.getAssetsDD();
              }
            }
          });
        }
      }
    }, 500);
  }

  /**
   * Handles combobox close event.
   * Cleans up the active scroll listener to prevent memory leaks and redundant calls.
   */
  onComboboxClosed(): void {
    this.isOpened = false;
    if (this.clearTimeout) clearTimeout(this.clearTimeout);
    if (this._scrollUnlisten) {
      this._scrollUnlisten();
      this._scrollUnlisten = undefined;
    }
  }

  /**
   * Debounced filter handler for the Asset ComboBox.
   * Resets pagination to page 1 and queries backend with the search term.
   * @param value Search string
   */
  handleAssetFilter(value: string): void {
    this._assetComboScroll = false;
    if (this._assetFilterTimeout) clearTimeout(this._assetFilterTimeout);
    this._assetFilterTimeout = setTimeout(() => {
      const term = value ? value.trim() : '';
      this._assetComboBoxPagingParams.search = term;
      this._assetComboBoxPagingParams.currentItem = '0';
      this._assetComboBoxPagingParams.page = 1;
      this.getAssetsDD();
    }, 500);
  }

  /**
   * In-memory search filter handler for the Person ComboBox.
   * @param value Search string
   */
  handlePersonFilter(value: string): void {
    if (!value) {
      this.personsDataFiltered = [...this.personsData];
    } else {
      const term = value.toLowerCase().trim();
      this.personsDataFiltered = this.personsData.filter(
        (p) =>
          (p.code && p.code.toLowerCase().includes(term)) ||
          (p.firstName && p.firstName.toLowerCase().includes(term)) ||
          (p.lastName && p.lastName.toLowerCase().includes(term)) ||
          (p.displayName && p.displayName.toLowerCase().includes(term)) ||
          (p.fullName && p.fullName.toLowerCase().includes(term))
      );
    }
  }

  /**
   * Loads all active/unperformed schedules to populate the schedule dropdown.
   */
  loadAllSchedules(): void {
    this.scheduleService.getAllSchedules({ isPerformed: false }).subscribe({
      next: (res: any) => {
        const raw = Array.isArray(res) ? res : (res?.data || res?.body || []);
        this.allOpenSchedules = (raw || [])
          .filter((s: any) => !s.isPerformed)
          .map((s: any) => ({
            ...s,
            displayTitle: `${s.maintenanceCode ? s.maintenanceCode + ' - ' : ''}${s.maintenanceTypeName || ('Schedule #' + s.id)}${s.assetNumber ? ' [' + s.assetNumber + ']' : ''}`
          }));
        this.updateAvailableSchedules();
      },
      error: () => {
        this.allOpenSchedules = [];
        this.openSchedules = [];
        this.openSchedulesFiltered = [];
      }
    });
  }

  /**
   * Filters the available schedules based on selected asset (if any) and active status.
   */
  updateAvailableSchedules(): void {
    const assetId = this._form?.get('assetId')?.value;
    const currentSchedId = this._form?.get('maintenanceAssetScheduleId')?.value;

    if (assetId) {
      this.openSchedules = this.allOpenSchedules.filter((s) => s.assetId === assetId && (!s.isPerformed || s.id === currentSchedId));
    } else {
      this.openSchedules = this.allOpenSchedules.filter((s) => !s.isPerformed || s.id === currentSchedId);
    }

    if (currentSchedId && !this.openSchedules.some((s) => s.id === currentSchedId)) {
      const selected = this.allOpenSchedules.find((s) => s.id === currentSchedId) || this.selectedSchedule;
      if (selected) {
        this.openSchedules.unshift(selected);
      }
    }

    this.openSchedulesFiltered = [...this.openSchedules];

    if (currentSchedId) {
      const found = this.allOpenSchedules.find((s) => s.id === currentSchedId);
      if (found) {
        this.selectedSchedule = found;
      }
    }
  }

  /**
   * Filter handler for the Schedule ComboBox search.
   */
  handleScheduleFilter(value: string): void {
    if (!value) {
      this.openSchedulesFiltered = [...this.openSchedules];
    } else {
      const term = value.toLowerCase().trim();
      this.openSchedulesFiltered = this.openSchedules.filter(
        (s) =>
          (s.maintenanceCode && s.maintenanceCode.toLowerCase().includes(term)) ||
          (s.maintenanceTypeName && s.maintenanceTypeName.toLowerCase().includes(term)) ||
          (s.assetNumber && s.assetNumber.toLowerCase().includes(term)) ||
          (s.displayTitle && s.displayTitle.toLowerCase().includes(term))
      );
    }
  }

  /**
   * Invoked when an asset is chosen.
   * Fetches unperformed maintenance schedules for this asset and checks blacklist/overdue status.
   * @param assetId The selected asset ID
   */
  onAssetSelected(assetId: number): void {
    this.scheduleService.getAllSchedules({ assetId, isPerformed: false }).subscribe({
      next: (schedules: any) => {
        const raw = Array.isArray(schedules) ? schedules : (schedules?.data || schedules?.body || []);
        const mapped = (raw || [])
          .filter((s: any) => !s.isPerformed)
          .map((s: any) => ({
            ...s,
            displayTitle: `${s.maintenanceCode ? s.maintenanceCode + ' - ' : ''}${s.maintenanceTypeName || ('Schedule #' + s.id)}${s.assetNumber ? ' [' + s.assetNumber + ']' : ''}`
          }));

        const existingIds = new Set(this.allOpenSchedules.map((s) => s.id));
        mapped.forEach((m: any) => {
          if (!existingIds.has(m.id)) {
            this.allOpenSchedules.push(m);
          }
        });

        const currentSchedId = this._form.get('maintenanceAssetScheduleId')?.value;
        const currentSched = currentSchedId ? (this.allOpenSchedules.find((s) => s.id === currentSchedId) || this.selectedSchedule) : null;

        if (currentSched && !mapped.some((s: any) => s.id === currentSchedId)) {
          if (currentSched.assetId === assetId || !currentSched.assetId) {
            mapped.unshift(currentSched);
          }
        }

        this.openSchedules = mapped;
        this.openSchedulesFiltered = [...this.openSchedules];

        if (currentSchedId) {
          this.selectedSchedule = this.allOpenSchedules.find((s) => s.id === currentSchedId) || currentSched || null;
          if (!this.selectedSchedule && !this.isPerformAction && this._pageState === PageState.ADD) {
            this._form.patchValue({ maintenanceAssetScheduleId: null }, { emitEvent: false });
          }
        }
      }
    });

    this.performService.checkCheckoutBlacklist(assetId).subscribe({
      next: (blacklist) => {
        this.blacklistInfo = blacklist?.isBlocked ? blacklist : null;
      }
    });
  }

  /**
   * Computed getter property calculating total maintenance cost: labor cost + parts cost.
   */
  get totalCost(): number {
    const labor = Number(this._form.get('laborCost')?.value) || 0;
    const parts = Number(this._form.get('partsCost')?.value) || 0;
    return labor + parts;
  }

  // #region Asset Popup Selector
  /**
   * Opens the asset grid lookup popup modal.
   */
  _openAssetsList(): void {
    this._assetsListEnabled = true;
  }

  /**
   * Closes the asset grid lookup popup modal.
   */
  _closeAssetsList(): void {
    this._assetsListEnabled = false;
  }

  /**
   * Confirms the asset selected from the lookup popup grid and updates the reactive form.
   * @param e Popup confirm event
   */
  _returnSelectedAssetsHandler(e?: any): void {
    if (this._selectedAssets && this._selectedAssets.length > 0) {
      const selectedId = this._selectedAssets[0];
      const found = this.assetsData.find((a) => a.id === selectedId);
      if (!found) {
        this.assetService.getAssetById(selectedId).subscribe((asset) => {
          if (asset) {
            this.assetsData.push(asset);
            this.assetsDataFiltered = [...this.assetsData];
            this._form.patchValue({ assetId: selectedId });
          }
        });
      } else {
        this._form.patchValue({ assetId: selectedId });
      }
    }
    this._closeAssetsList();
  }

  /**
   * Tracks asset row selections in the asset lookup popup grid.
   * @param e Array of selected IDs
   */
  _onAssetsRowSelectionChanges(e: any[]): void {
    this._selectedAssets = [...e];
  }

  /**
   * Data provider for the asset lookup popup grid.
   */
  _getAssets = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this.assetService.getAssetGrid(params, model);
  };
  // #endregion

  // #region Person Popup Selector
  /**
   * Opens the person/technician grid lookup popup modal.
   */
  _openPersonsList(): void {
    this._personsListEnabled = true;
  }

  /**
   * Closes the person/technician grid lookup popup modal.
   */
  _closePersonsList(): void {
    this._personsListEnabled = false;
  }

  /**
   * Confirms the person selected from the lookup popup grid and updates the reactive form.
   * @param e Popup confirm event
   */
  _returnSelectedPersonsHandler(e?: any): void {
    if (this._selectedPerson) {
      this._form.patchValue({ personId: this._selectedPerson });
    }
    this._closePersonsList();
  }

  /**
   * Tracks person row selections in the person lookup popup grid.
   * @param e Array of selected IDs
   */
  _onPersonsRowSelectionChanges(e: any[]): void {
    this._selectedPerson = e[0];
  }

  /**
   * Data provider for the person lookup popup grid.
   */
  _getPersons = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    return this.usersService.getPersonsGridData(params, model);
  };
  // #endregion

  /**
   * Validates form and submits the maintenance execution payload to the backend API.
   * On success:
   * - Notifies the user via LayoutComponent popup.
   * - Automatically advances recurrence if requested.
   * - Refreshes parent grids and optionally closes modal based on action.
   * @param action 'save' to keep dialog open, 'saveAndClose' to dismiss modal on success
   */
  _onSave(action: 'save' | 'saveAndClose' = 'saveAndClose'): void {
    if (this._form.invalid) {
      this._invalidForm = true;
      this._form.markAllAsTouched();
      this._layoutComponent.openPopup({
        title: 'Validation Error',
        message: 'Please fill in all required fields marked with *.',
        withoutConfirm: true
      });
      return;
    }

    this._invalidForm = false;
    this.isSubmitting = true;
    const val = this._form.getRawValue();
    const sharedModel = this._sharedService?.model;
    const raw = sharedModel?.model !== undefined ? sharedModel.model : sharedModel;
    const editId = this._model?.id || raw?.id;
debugger
    if (editId) {
      const updatePayload: PerformMaintenanceActionModel = {
        id: editId,
        personId: val.personId ? Number(val.personId) : null,
        laborCost: val.laborCost != null && val.laborCost !== '' ? Number(val.laborCost) : undefined,
        partsCost: val.partsCost != null && val.partsCost !== '' ? Number(val.partsCost) : undefined,
        notes: val.notes,
        maintenanceNotes: val.maintenanceNotes
      };

      this.performService.performAction(updatePayload).subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this._layoutComponent.openPopup({
            title: 'Success',
            message: 'Maintenance transaction has been updated successfully.',
            withoutConfirm: true
          });

          this.saved.emit(res);
          this._sharedService.ActivateOrDeactivate.emit(true);

          if (action === 'saveAndClose') {
            this._closeForm('saveAndClose', res);
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          const msg =
            err?.error?.data?.message ||
            err?.error?.message ||
            err?.message ||
            'Error updating maintenance transaction.';
          this._layoutComponent.openPopup({
            title: 'Alert',
            message: msg,
            withoutConfirm: true
          });
        }
      });
      return;
    }

    const payload: PerformMaintenanceRequestModel = {
      maintenanceCode: val.maintenanceCode || undefined,
      maintenanceTypeId: val.maintenanceTypeId ? Number(val.maintenanceTypeId) : undefined,
      timeInterval: val.timeInterval !== undefined ? Boolean(val.timeInterval) : true,
      performNonDate: val.performNonDate != null && val.performNonDate !== '' ? Number(val.performNonDate) : undefined,
      measureWhenPerformedId: val.measureWhenPerformedId ? Number(val.measureWhenPerformedId) : undefined,
      assetId: val.assetId,
      maintenanceAssetScheduleId: val.maintenanceAssetScheduleId || null,
      personId: val.personId || null,
      performDate:
        val.performDate ||
        (this._sharedService?.getTodayInUserTimezone ? this._sharedService.getTodayInUserTimezone() : new Date()),
      laborCost: val.laborCost ? Number(val.laborCost) : 0,
      partsCost: val.partsCost ? Number(val.partsCost) : 0,
      advanceRecurrence: val.advanceRecurrence ?? true,
      notes: val.notes,
      maintenanceNotes: val.maintenanceNotes,
      documentFileIds:
        this._performedFilesDoc
          ?.map((f: any) => f.fileId || f.id || f.file?.id)
          .filter((id: any) => id != null && id > 0) || []
    };

    this.performService.performMaintenance(payload).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        const resAny = res as any;
        const savedId = resAny?.data?.id || resAny?.data || resAny?.id || (typeof res === 'number' ? res : 0);
        if (savedId) {
          this._model = { id: savedId };
          this.note?.createNote(savedId);
        }

        const msg = `Maintenance transaction has been processed successfully.${
          payload.advanceRecurrence && this.selectedSchedule?.isRecurring
            ? ' Recurrence advanced to next interval.'
            : ''
        }`;
        this._layoutComponent.openPopup({
          title: 'Success',
          message: msg,
          withoutConfirm: true
        });

        this.saved.emit(res);
        this._sharedService.ActivateOrDeactivate.emit(true);

        if (action === 'saveAndClose') {
          this._closeForm('saveAndClose', res);
        }
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg =
          err?.error?.data?.message ||
          err?.error?.message ||
          err?.message ||
          'Error processing maintenance transaction.';
        this._layoutComponent.openPopup({
          title: 'Alert',
          message: msg,
          withoutConfirm: true
        });
      }
    });
  }

  /**
   * Auto-generates a maintenance transaction barcode/code.
   */
  _generateBarcodeClick(): void {
    this._barcodeGeneratorsService.getBarcode('perform-maintenance', 1).subscribe({
      next: (data: any) => {
        const val = data?.result ? data.result.toString() : (data?.data?.result ? data.data.result.toString() : (data?.data ? data.data.toString() : (Array.isArray(data) && data[0] ? data[0].toString() : '')));
        if (val) {
          this._form.patchValue({ maintenanceCode: val });
        }
      },
      error: () => {}
    });
  }

  /**
   * Loads maintenance types from backend to populate dropdown.
   */
  loadMaintenanceTypes(): void {
    this.scheduleService.getMaintenanceTypes().subscribe({
      next: (types: any) => {
        const raw = Array.isArray(types) ? types : (types?.data || []);
        const existingMap = new Map(this.maintenanceTypes.map((t) => [t.id, t]));
        raw.forEach((t: any) => existingMap.set(t.id, t));
        this.maintenanceTypes = Array.from(existingMap.values());
        this.maintenanceTypesFiltered = [...this.maintenanceTypes];
      }
    });
  }

  /**
   * Filters maintenance types dropdown by typed search text.
   * @param value Search string
   */
  handleMaintenanceTypeFilter(value: string): void {
    if (!value) {
      this.maintenanceTypesFiltered = [...this.maintenanceTypes];
    } else {
      const term = value.toLowerCase().trim();
      this.maintenanceTypesFiltered = this.maintenanceTypes.filter(
        (t) =>
          (t.maintenanceTypeCode && t.maintenanceTypeCode.toLowerCase().includes(term)) ||
          (t.maintenanceTypeName && t.maintenanceTypeName.toLowerCase().includes(term))
      );
    }
  }

  /**
   * Loads measures when performed lookup items from backend API.
   * Uses fallback list if API is unreachable or empty.
   */
  loadMeasuresWhenPerformed(): void {
    this.performService.getMeasuresWhenPerformed().subscribe({
      next: (res: any) => {
        const raw = Array.isArray(res) ? res : (res?.data || res?.body || []);
        const list = raw && raw.length > 0 ? raw : this.getDefaultMeasures();
        const existingMap = new Map(this.measuresWhenPerformed.map((m) => [m.id, m]));
        list.forEach((m: any) => existingMap.set(m.id, m));
        this.measuresWhenPerformed = Array.from(existingMap.values());
        this.measuresWhenPerformedFiltered = [...this.measuresWhenPerformed];
      },
      error: () => {
        const list = this.getDefaultMeasures();
        const existingMap = new Map(this.measuresWhenPerformed.map((m) => [m.id, m]));
        list.forEach((m: any) => existingMap.set(m.id, m));
        this.measuresWhenPerformed = Array.from(existingMap.values());
        this.measuresWhenPerformedFiltered = [...this.measuresWhenPerformed];
      }
    });
  }

  /**
   * Fallback measures when performed matching reference configuration.
   */
  private getDefaultMeasures(): MeasureWhenPerformedDto[] {
    return [
      { id: 1, code: 'Licenses', name: 'Licenses' },
      { id: 2, code: 'Maint. Contract Cost', name: 'Maint. Contract Cost' },
      { id: 3, code: 'Mileage', name: 'Mileage' },
      { id: 4, code: 'Scrap Value', name: 'Scrap Value' },
      { id: 5, code: 'number', name: 'number' },
      { id: 6, code: 'Num', name: 'Num' },
      { id: 7, code: 'DEF', name: 'DEF' },
      { id: 8, code: 'SDW', name: 'SDW' },
      { id: 9, code: 'ASQW1', name: 'ASQW1' },
      { id: 10, code: 'ASQWS', name: 'ASQWS' }
    ];
  }

  /**
   * Filters measures when performed combobox options by search text.
   * @param value Search string
   */
  handleMeasureFilter(value: string): void {
    if (!value) {
      this.measuresWhenPerformedFiltered = [...this.measuresWhenPerformed];
    } else {
      const term = value.toLowerCase().trim();
      this.measuresWhenPerformedFiltered = this.measuresWhenPerformed.filter(
        (m) =>
          (m.name && m.name.toLowerCase().includes(term)) ||
          (m.code && m.code.toLowerCase().includes(term))
      );
    }
  }

  /**
   * Opens dialog to create a new Maintenance Type.
   */
  openAddType(): void {
    this.openUpsert(PageNames.MaintenanceType, null, PageState.ADD);
  }

  /**
   * Opens dialog to edit the currently selected Maintenance Type.
   */
  openEditSelectedType(): void {
    const selectedId = this._form.get('maintenanceTypeId')?.value;
    if (!selectedId) return;
    const existing = this.maintenanceTypes.find((t) => t.id === selectedId);
    if (existing) {
      this.openUpsert(PageNames.MaintenanceType, existing, PageState.EDIT);
    }
  }

  /**
   * Launches an inline upsert dialog via asap-dialog-layout.
   * @param componentName Target component name
   * @param model Model payload
   * @param pageState PageState ADD or EDIT
   */
  openUpsert(componentName: string, model: any, pageState: PageState): void {
    this._dialogComponentName = componentName;
    this._dialogModel = { model, pageState };
    this._isDialiog = true;
  }

  /**
   * Closes nested modal dialog and refreshes lookup datasets.
   * @param event Dialog close payload
   */
  _closeDialog(event: any): void {
    this._isDialiog = false;
    if (event?.component === PageNames.MaintenanceType || event?.action === 'saveAndClose' || event?.action === 'save') {
      this.loadMaintenanceTypes();
      if (event?.response?.id) {
        this._form.patchValue({ maintenanceTypeId: event.response.id });
      }
    }
  }

  /**
   * Closes the form and emits close events to parent and shared service dialog buses.
   * @param action Close action identifier
   * @param response Optional server response payload
   */
  _closeForm(action?: string, response?: any): void {
    const closeComponent = {
      isClosed: true,
      open: false,
      component: 'perform-maintenance',
      action: action,
      response: response
    };
    this._sharedService.closeDialog.emit(closeComponent);
  }

  /**
   * Cancel button handler. Dismisses the form without saving changes.
   */
  onCancel(): void {
    this._closeForm('close');
  }
}

